import React, { useState } from "react";
import Modal from "./Modal";
import { DESTINATIONS } from "../data/destinations";
import { Sparkles, ArrowRight, Wand2 } from "lucide-react";
import { rupiah } from "../lib/format";

const MOODS = [
  { id: "romantis", label: "Romantis" },
  { id: "santai", label: "Santai" },
  { id: "seru", label: "Seru" },
  { id: "tenang", label: "Tenang" },
  { id: "kreatif", label: "Kreatif" },
];
const BUDGETS = [
  { id: "hemat", label: "Hemat", note: "< Rp120rb" },
  { id: "sedang", label: "Sedang", note: "Rp120–220rb" },
  { id: "premium", label: "Spesial", note: "> Rp220rb" },
];
const ACTS = ["Semua", "Museum", "Crafting", "Restaurant", "Wisata Alam", "Rides"];

export default function RecommendModal({ open, onClose, city, onPick }) {
  const [mood, setMood] = useState("romantis");
  const [budget, setBudget] = useState("sedang");
  const [act, setAct] = useState("Semua");
  const [results, setResults] = useState(null);

  const run = () => {
    let list = DESTINATIONS.filter((d) => d.city === city);
    const scored = list
      .map((d) => {
        let score = 0;
        if (d.moods.includes(mood)) score += 3;
        if (d.budget === budget) score += 2;
        if (act !== "Semua" && d.category === act) score += 3;
        score += d.rating - 4.5;
        return { d, score };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 3)
      .map((x) => x.d);
    setResults(scored);
  };

  const reset = () => { setResults(null); };

  return (
    <Modal open={open} onClose={() => { onClose(); reset(); }} title="Bantu pilihkan" testId="recommend-modal">
      <div className="p-5">
        {!results ? (
          <>
            <p className="mb-5 flex items-start gap-2 rounded-2xl bg-[#fff4ef] p-3 text-[13px] text-[#8a6f66]">
              <Sparkles className="mt-0.5 h-4 w-4 flex-none text-[#ff6b4a]" />
              Ceritakan suasana yang kalian mau di <b className="mx-1">{city}</b>, biar kami pilihkan tempat paling pas.
            </p>

            <p className="mb-2 text-[12px] font-bold uppercase tracking-wide text-[#ff5a5f]">Suasana</p>
            <div className="mb-5 flex flex-wrap gap-2">
              {MOODS.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMood(m.id)}
                  data-testid={`rec-mood-${m.id}`}
                  className={`rounded-full border px-4 py-2 text-[13px] font-semibold transition-colors ${
                    mood === m.id ? "border-[#ff5a5f] bg-[#ffebeb] text-[#ff5a5f]" : "border-[#e9e0d5] text-[#7c7277]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <p className="mb-2 text-[12px] font-bold uppercase tracking-wide text-[#ff5a5f]">Budget</p>
            <div className="mb-5 grid grid-cols-3 gap-2">
              {BUDGETS.map((b) => (
                <button
                  key={b.id}
                  onClick={() => setBudget(b.id)}
                  data-testid={`rec-budget-${b.id}`}
                  className={`rounded-2xl border px-3 py-3 text-center transition-colors ${
                    budget === b.id ? "border-[#ff5a5f] bg-[#ffebeb]" : "border-[#e9e0d5]"
                  }`}
                >
                  <div className="text-[13px] font-bold text-[#1e1b18]">{b.label}</div>
                  <div className="text-[11px] text-[#a89f98]">{b.note}</div>
                </button>
              ))}
            </div>

            <p className="mb-2 text-[12px] font-bold uppercase tracking-wide text-[#ff5a5f]">Aktivitas</p>
            <div className="mb-6 flex flex-wrap gap-2">
              {ACTS.map((a) => (
                <button
                  key={a}
                  onClick={() => setAct(a)}
                  data-testid={`rec-act-${a}`}
                  className={`rounded-full border px-3.5 py-2 text-[13px] font-semibold transition-colors ${
                    act === a ? "border-[#ff5a5f] bg-[#ffebeb] text-[#ff5a5f]" : "border-[#e9e0d5] text-[#7c7277]"
                  }`}
                >
                  {a}
                </button>
              ))}
            </div>

            <button
              onClick={run}
              data-testid="rec-submit"
              className="flex w-full items-center justify-center gap-2 rounded-full kk-coral-gradient py-3.5 text-[15px] font-bold text-white transition-transform active:scale-[0.98]"
            >
              <Wand2 className="h-4 w-4" /> Carikan tempat kencan
            </button>
          </>
        ) : (
          <>
            <p className="mb-4 text-[13px] text-[#8a8278]">Ini pilihan paling pas untuk kalian:</p>
            <div className="space-y-3">
              {results.map((d) => (
                <button
                  key={d.id}
                  onClick={() => { onPick(d); onClose(); reset(); }}
                  data-testid={`rec-result-${d.id}`}
                  className="flex w-full items-center gap-3 rounded-2xl border border-[#efeae4] bg-white p-3 text-left transition-colors hover:border-[#f0d3da]"
                >
                  <img src={d.img} alt={d.name} className="h-16 w-20 flex-none rounded-xl object-cover" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] font-bold">{d.name}</div>
                    <div className="truncate text-[12px] text-[#a89f98]">{d.category} · {d.area} · {rupiah(d.price)}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 flex-none text-[#ff5a5f]" />
                </button>
              ))}
            </div>
            <button
              onClick={reset}
              className="mt-4 w-full rounded-full border border-[#e9e0d5] py-3 text-[14px] font-semibold text-[#7c7277]"
              data-testid="rec-again"
            >
              Ubah preferensi
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}
